// components/events/EventDetailSidePanel.tsx
// -----------------------------------------------------------------------------
// Event detail components:
// - EventDetailContent: Shared content (data fetching, RSVP, all sections)
// - EventDetailSidePanel: Wraps content in a SidePanel (used from club page)
// Supports two layouts: "event" (default) with image, RSVP, date card,
// attendees; and "hang" with avatars, auto-title, countdown row, no RSVP.
// -----------------------------------------------------------------------------

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { lowercaseMeridiem, userDisplayName, formatHangTitle } from "@tinyclub/shared/utils";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import {
  SidePanelStack,
  SidePanelStackContent,
  useSidePanelStack,
  useSidePanelStackOptional,
} from "@/components/ui/SidePanelStack";
import { EventFormContent } from "@/components/events/CreateEventForm";
import {
  useFetchEventByIdQuery,
  useFetchUserProfileQuery,
  useFetchUserProfilesByIdsQuery,
  useGetClubQuery,
  useCreateEventAttendeeMutation,
  useDeleteEventAttendeeMutation,
  useCreateEventReactionMutation,
  useDeleteEventReactionMutation,
  useDeleteEventMutation,
  type EventAttendee,
  type UserProfile,
  RsvpState,
  EventType,
} from "@/api/clubsApi";
import { useAuth } from "@/components/providers/AuthProvider";
import { useLoginModal } from "@/components/auth/LoginModalProvider";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import { WebMediaViewer } from "@/components/ui/WebMediaViewer";
import { LocationView } from "@/components/location";
import { EventReactionButton } from "@/components/events/EventReactionButton";
import { cn } from "@/components/ui/cn";
import { ShareButton } from "@/components/ui/ShareButton";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { useFetchClubMembersQuery } from "@/api/clubsApi";
import { DeleteConfirmDialog } from "@/components/ui/DeleteConfirmDialog";
import { generateGoogleCalendarUrl, downloadIcsFile } from "@/utils/calendarUtils";

// ---------------------------------------------------------------------------
// EventDetailContent — shared content used by both SidePanel and standalone
// ---------------------------------------------------------------------------

interface EventDetailContentProps {
  eventId: string;
  onClose?: () => void;
}

export function EventDetailContent({ eventId, onClose }: EventDetailContentProps) {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const { openLoginModal } = useLoginModal();
  const navigate = useNavigate();
  const pendingRsvpRef = useRef<RsvpState | null>(null);

  // Media viewer state
  const [viewerMedia, setViewerMedia] = useState<{
    src: string;
    type: "image" | "video";
  } | null>(null);

  // Fetch event data
  const { data: event, isLoading } = useFetchEventByIdQuery(eventId);
  const rootEvent = event?.rootEvent;
  const isHang = rootEvent?.event_type === EventType.hang;

  // Fetch creator profile
  const { data: creatorProfile } = useFetchUserProfileQuery(
    rootEvent?.creator_id ?? "",
    { skip: !rootEvent?.creator_id }
  );

  // Fetch club data
  const { data: club } = useGetClubQuery(rootEvent?.club_id ?? "", {
    skip: !rootEvent?.club_id,
  });

  // Fetch club members to determine if user is manager (owner/cohost)
  const { data: clubMembers } = useFetchClubMembersQuery(
    { clubId: rootEvent?.club_id ?? "", includeUserProfiles: false },
    { skip: !rootEvent?.club_id }
  );

  // Determine if current user is a manager (event creator OR club owner/cohost)
  const isEventCreator = currentUserId === rootEvent?.creator_id;
  const isManager = useMemo(() => {
    if (isEventCreator) return true;
    if (!currentUserId || !clubMembers) return false;
    const userMember = clubMembers.find((m) => m.user_id === currentUserId);
    return userMember?.role === "owner" || userMember?.role === "cohost";
  }, [isEventCreator, currentUserId, clubMembers]);

  // RSVP mutations
  const [createAttendee, { isLoading: isCreating }] =
    useCreateEventAttendeeMutation();
  const [deleteAttendee, { isLoading: isDeleting }] =
    useDeleteEventAttendeeMutation();
  const isRsvpLoading = isCreating || isDeleting;

  // End/cancel hang
  const [deleteEvent, { isLoading: isDeletingEvent }] = useDeleteEventMutation();
  const [showEndHangDialog, setShowEndHangDialog] = useState(false);

  // Reaction mutations (hang events)
  const [createReaction] = useCreateEventReactionMutation();
  const [deleteReaction] = useDeleteEventReactionMutation();
  const [optimisticReactionActive, setOptimisticReactionActive] = useState(false);
  const [optimisticReactionId, setOptimisticReactionId] = useState<string | null>(null);

  const reactions = event?.event_reactions || [];
  const userReaction = useMemo(
    () => (currentUserId ? reactions.find((r) => r.user_id === currentUserId) ?? null : null),
    [reactions, currentUserId]
  );
  const isReactionActive = !!userReaction || optimisticReactionActive;

  // Sync optimistic state with server
  useEffect(() => {
    if (userReaction) {
      setOptimisticReactionActive(false);
      setOptimisticReactionId(userReaction.id);
    } else if (!optimisticReactionActive) {
      setOptimisticReactionId(null);
    }
  }, [userReaction, optimisticReactionActive]);

  const handleActivateReaction = async () => {
    if (!event?.id || !currentUserId) {
      openLoginModal();
      return;
    }
    setOptimisticReactionActive(true);
    try {
      const result = await createReaction({
        occurrenceId: event.id,
        reactionType: "custom_handsUp",
      }).unwrap();
      const newReaction = Array.isArray(result) ? result[0] : result;
      setOptimisticReactionId(newReaction.id);
    } catch (error) {
      console.error("Error creating reaction:", error);
      setOptimisticReactionActive(false);
      setOptimisticReactionId(null);
    }
  };

  const handleDeactivateReaction = async () => {
    const reactionId = userReaction?.id || optimisticReactionId;
    if (!reactionId || !event?.id) return;
    setOptimisticReactionActive(false);
    try {
      await deleteReaction({ reactionId, occurrenceId: event.id }).unwrap();
    } catch (error) {
      console.error("Error deleting reaction:", error);
      setOptimisticReactionActive(true);
    }
  };

  // Attendees
  const attendees = event?.event_attendees || [];
  const userAttendee = currentUserId
    ? attendees.find((a: EventAttendee) => a.user_id === currentUserId)
    : undefined;
  const userRsvpStatus = userAttendee?.rsvp_status;

  const goingAttendees = attendees.filter(
    (a: EventAttendee) => a.rsvp_status === RsvpState.yes
  );
  const goingCount = goingAttendees.length;

  // Fetch going attendee profiles for the preview display
  const goingUserIds = useMemo(
    () => goingAttendees.map((a) => a.user_id).filter(Boolean) as string[],
    [goingAttendees]
  );
  const { data: goingProfiles = [] } = useFetchUserProfilesByIdsQuery(
    goingUserIds,
    { skip: goingUserIds.length === 0 }
  );

  // RSVP handler
  const handleRsvp = async (status: RsvpState) => {
    if (!currentUserId) {
      pendingRsvpRef.current = status;
      sessionStorage.setItem(
        "pendingRsvp",
        JSON.stringify({ eventId, status })
      );
      openLoginModal();
      return;
    }
    if (!eventId) return;

    if (userRsvpStatus === status && userAttendee) {
      await deleteAttendee({
        attendeeId: userAttendee.id,
        occurrenceId: eventId,
      });
    } else {
      await createAttendee({
        occurrenceId: eventId,
        userId: currentUserId,
        rsvpStatus: status,
      });
    }
  };

  // After login, execute pending RSVP
  useEffect(() => {
    if (!user) return;

    if (pendingRsvpRef.current) {
      const status = pendingRsvpRef.current;
      pendingRsvpRef.current = null;
      handleRsvp(status);
      return;
    }

    const raw = sessionStorage.getItem("pendingRsvp");
    if (raw) {
      try {
        const pending = JSON.parse(raw);
        if (pending.eventId === eventId && pending.status) {
          sessionStorage.removeItem("pendingRsvp");
          handleRsvp(pending.status as RsvpState);
        }
      } catch {
        sessionStorage.removeItem("pendingRsvp");
      }
    }
  }, [user]);

  // Date/time formatting
  const eventTimezone = rootEvent?.timezone ?? undefined;
  const startDateTime = event?.occurrence_start
    ? DateTime.fromISO(event.occurrence_start, { zone: eventTimezone })
    : DateTime.now();
  const endDateTime = event?.occurrence_end
    ? DateTime.fromISO(event.occurrence_end, { zone: eventTimezone })
    : null;

  // Figma format: "EEEE, MMMM d" (date) + "h:mm a - h:mm a" (time range)
  const isCurrentYear = startDateTime.year === DateTime.now().year;
  const datePart = startDateTime.toFormat(isCurrentYear ? "EEEE, MMMM d" : "EEEE, MMMM d, yyyy");
  const startTimePart = lowercaseMeridiem(startDateTime.toFormat("h:mm a"));
  let timeRangePart = startTimePart;
  if (endDateTime) {
    timeRangePart = `${startTimePart} - ${lowercaseMeridiem(endDateTime.toFormat("h:mm a"))}`;
  }

  // Countdown text for hang events
  const countdownText = useMemo(() => {
    if (!isHang) return "";
    const now = DateTime.now();
    if (endDateTime && now > endDateTime) {
      const diff = now.diff(endDateTime, ["hours", "minutes"]);
      const h = Math.floor(diff.hours);
      const m = Math.floor(diff.minutes);
      if (h > 0) return `Ended ${h}h ${m}m ago`;
      return `Ended ${m}m ago`;
    }
    if (now >= startDateTime && (!endDateTime || now <= endDateTime)) {
      return "Happening now";
    }
    const diff = startDateTime.diff(now, ["hours", "minutes"]);
    const h = Math.floor(diff.hours);
    const m = Math.floor(diff.minutes);
    if (h > 0) return `Starts in ${h}h ${m}m`;
    return `Starts in ${m}m`;
  }, [isHang, startDateTime, endDateTime]);

  // Hang lifecycle status
  const now = DateTime.now();
  const isOngoing = isHang && now >= startDateTime && (!endDateTime || now <= endDateTime);
  const hasEnded = isHang && endDateTime && now > endDateTime;
  const canEndHang = isHang && isEventCreator && !hasEnded;

  // Hang title — auto-generated from creator name, location, and time range
  const hangTitle = useMemo(() => {
    if (!isHang) return "";
    const locationCustomName = rootEvent?.location_name || "";
    const fallbackName =
      rootEvent?.location_object?.properties?.name_preferred ||
      rootEvent?.location_object?.properties?.name ||
      "";
    // Only combine custom name + fallback if they differ; otherwise just use one
    const displayLocation = locationCustomName && locationCustomName !== fallbackName
      ? `${locationCustomName}, ${fallbackName}`
      : locationCustomName || fallbackName;
    const isCurrentUser = currentUserId === rootEvent?.creator_id;
    return formatHangTitle(
      creatorProfile ?? null,
      displayLocation,
      rootEvent?.location_object?.properties?.full_address ||
        rootEvent?.location_object?.properties?.place_formatted ||
        "",
      startDateTime,
      endDateTime,
      isCurrentUser
    );
  }, [isHang, rootEvent, creatorProfile, currentUserId, startDateTime, endDateTime]);

  // Event image
  const eventImageUrl =
    rootEvent?.primary_image_medium ||
    rootEvent?.primary_image_full ||
    rootEvent?.primary_image_small;

  const hasLocation = Boolean(
    rootEvent?.location_object || rootEvent?.location_name
  );

  // Media click handler
  const handleMediaClick = () => {
    const fullImageUrl =
      rootEvent?.primary_image_full || rootEvent?.primary_image_medium;
    if (fullImageUrl) {
      setViewerMedia({ src: fullImageUrl, type: "image" });
    }
  };

  // Build attendees names text
  const attendeesNamesText = useMemo(() => {
    if (!goingProfiles || goingProfiles.length === 0) return "";
    const names = goingProfiles.slice(0, 3).map((p) => userDisplayName(p));
    const remaining = goingCount - names.length;
    if (remaining > 0) {
      return `${names.join(", ")} and ${remaining} ${remaining === 1 ? "other" : "others"}`;
    }
    return names.join(", ");
  }, [goingProfiles, goingCount]);

  if (isLoading) {
    return (
      <>
        <EventDetailSkeleton />
        {viewerMedia && (
          <WebMediaViewer
            src={viewerMedia.src}
            type={viewerMedia.type}
            onClose={() => setViewerMedia(null)}
          />
        )}
      </>
    );
  }

  if (!event || !rootEvent) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <Icon name="calendar" size={48} className="text-text-tertiary" />
        <p className="text-[18px] font-medium text-text-primary">
          Event not found
        </p>
        <p className="text-[16px] text-text-secondary text-center">
          This event may have been deleted or you don't have access to it.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-8">
        {/* Event image — event only */}
        {!isHang && eventImageUrl && (
          <div className="flex justify-center">
            <div
              className="w-[336px] max-w-full rounded-[16px] overflow-hidden border border-border-tertiary cursor-pointer"
              style={{ aspectRatio: "1 / 1" }}
              onClick={handleMediaClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleMediaClick();
                }
              }}
            >
              <img
                src={eventImageUrl}
                alt={rootEvent.title || "Event"}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Info sections */}
        <div className="flex flex-col gap-4">
          {isHang ? (
            /* ---- Hang layout ---- */
            <div className="flex flex-col gap-4">
              {/* Hang header: overlapping avatars */}
              <div className="flex items-start pr-2">
                {/* Hang bolt badge */}
                <div className="w-8 h-8 rounded-full bg-[#fceb6e] flex items-center justify-center flex-shrink-0 z-[2] mr-[-8px]">
                  <Icon name="bolt" size={14} className="text-text-primary" />
                </div>
                {/* Creator avatar */}
                {creatorProfile && (
                  <div className="w-8 h-8 rounded-full border border-border-tertiary overflow-hidden bg-surface-1 flex-shrink-0 z-[1]">
                    {creatorProfile.avatar_small || creatorProfile.avatar_medium ? (
                      <img
                        src={creatorProfile.avatar_small || creatorProfile.avatar_medium}
                        alt={userDisplayName(creatorProfile)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[13px] font-medium text-text-secondary">
                        {(creatorProfile.first_name?.[0] || "?").toUpperCase()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Hang title + description + countdown */}
              <div className="flex flex-col gap-2">
                <h2
                  className="text-text-primary"
                  style={{
                    fontSize: 28,
                    letterSpacing: -0.56,
                    lineHeight: 1.08,
                  }}
                >
                  {hangTitle || "Hang happening"}
                </h2>

                {rootEvent.description && (
                  <p className="text-[18px] text-text-secondary leading-[1.3]">
                    {rootEvent.description}
                  </p>
                )}

                {/* Countdown row */}
                <div className="flex items-center justify-between">
                  <div className="bg-surface rounded-[8px] flex gap-2 items-center h-10 p-[6px] text-text-secondary">
                    <Icon name="clock" size={20} />
                    <span className="text-[14px] font-medium">
                      {countdownText}
                    </span>
                  </div>
                  <EventReactionButton
                    isSelected={isReactionActive}
                    onActivate={handleActivateReaction}
                    onDeactivate={handleDeactivateReaction}
                  />
                </div>

                {/* Quick reply buttons */}
                {rootEvent.allows_quick_replies &&
                  currentUserId !== rootEvent.creator_id && (
                    <div className="flex gap-2 mt-2">
                      {!userRsvpStatus ||
                      userRsvpStatus === RsvpState.no ||
                      userRsvpStatus === RsvpState.none ? (
                        <>
                          <button
                            className="flex-1 flex items-center justify-center h-12 rounded-[81px] px-2 border border-border-secondary bg-gradient-to-b from-white to-[#fafaf5] to-[14.9%] opacity-80 hover:opacity-100 transition-all disabled:opacity-50"
                            style={{
                              boxShadow:
                                "0px 1px 8px 0px rgba(0,0,0,0.06), inset 0px 2px 3px 0px rgba(255,255,255,0.35)",
                            }}
                            disabled={isRsvpLoading}
                            onClick={() => handleRsvp(RsvpState.maybe)}
                          >
                            <span className="text-[16px] font-medium text-text-primary">
                              Hope to join
                            </span>
                          </button>
                          <button
                            className="flex-1 flex items-center justify-center h-12 rounded-[81px] px-2 border border-border-secondary bg-gradient-to-b from-white to-[#fafaf5] to-[14.9%] opacity-80 hover:opacity-100 transition-all disabled:opacity-50"
                            style={{
                              boxShadow:
                                "0px 1px 8px 0px rgba(0,0,0,0.06), inset 0px 2px 3px 0px rgba(255,255,255,0.35)",
                            }}
                            disabled={isRsvpLoading}
                            onClick={() => handleRsvp(RsvpState.yes)}
                          >
                            <span className="text-[16px] font-medium text-text-primary">
                              See you there!
                            </span>
                          </button>
                        </>
                      ) : (
                        <button
                          className="flex-1 flex items-center justify-center h-12 rounded-[81px] px-2 bg-surface transition-all disabled:opacity-50"
                          disabled={isRsvpLoading}
                          onClick={async () => {
                            if (userAttendee) {
                              await deleteAttendee({
                                attendeeId: userAttendee.id,
                                occurrenceId: eventId,
                              });
                            }
                          }}
                        >
                          <span className="text-[16px] font-medium text-blue">
                            {userRsvpStatus === RsvpState.yes
                              ? "You'll be there!"
                              : "You're hoping to join"}
                          </span>
                        </button>
                      )}
                    </div>
                  )}

              </div>
            </div>
          ) : (
            /* ---- Event layout ---- */
            <>
              {/* Title + Creator subgroup */}
              <div className="flex flex-col gap-3">
                {/* Event title */}
                <h2
                  className="text-text-primary"
                  style={{
                    fontSize: 28,
                    letterSpacing: -0.56,
                    lineHeight: 1.08,
                  }}
                >
                  {rootEvent.title || "Untitled Event"}
                </h2>

                {/* Creator row — below title */}
                {creatorProfile && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center pr-2">
                      {/* Creator avatar */}
                      <div className="w-8 h-8 rounded-full border border-border-tertiary overflow-hidden bg-surface-1 flex-shrink-0 z-[2] mr-[-8px]">
                        {creatorProfile.avatar_small || creatorProfile.avatar_medium ? (
                          <img
                            src={creatorProfile.avatar_small || creatorProfile.avatar_medium}
                            alt={userDisplayName(creatorProfile)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[13px] font-medium text-text-secondary">
                            {(creatorProfile.first_name?.[0] || "?").toUpperCase()}
                          </div>
                        )}
                      </div>
                      {/* Club badge overlapping */}
                      {club && (
                        <div
                          className="w-8 h-8 rounded-[8px] overflow-hidden flex-shrink-0 z-[1] flex items-center justify-center"
                          style={{
                            backgroundColor: club.theme || "#6366f1",
                          }}
                        >
                          {club.primary_image_small || club.primary_image_medium ? (
                            <img
                              src={club.primary_image_small || club.primary_image_medium}
                              alt={club.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-[13px] font-medium text-white">
                              {club.name?.[0]?.toUpperCase() || "C"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-[16px] text-text-secondary leading-[1.3]">
                      {userDisplayName(creatorProfile)}
                      {club && (
                        <>
                          {" in "}
                          <Link to="/clubs/$identifier" params={{ identifier: club.id }} className="font-medium hover:underline">{club.name}</Link>
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* RSVP buttons — event only */}
              <div className="flex gap-2">
                <RsvpButton
                  emoji="👍"
                  label="Going"
                  active={userRsvpStatus === RsvpState.yes}
                  disabled={isRsvpLoading}
                  onClick={() => handleRsvp(RsvpState.yes)}
                />
                <RsvpButton
                  emoji="🤷‍♂️"
                  label="Maybe"
                  active={userRsvpStatus === RsvpState.maybe}
                  disabled={isRsvpLoading}
                  onClick={() => handleRsvp(RsvpState.maybe)}
                />
                <RsvpButton
                  emoji="😢"
                  label="Not going"
                  active={userRsvpStatus === RsvpState.no}
                  disabled={isRsvpLoading}
                  onClick={() => handleRsvp(RsvpState.no)}
                />
              </div>

              {/* Date/time card — event only */}
              <div className="border border-border-tertiary rounded-[16px] p-3 flex items-center justify-between">
                <div className="flex flex-1 gap-4 items-center">
                  <div className="w-12 h-12 rounded-[12px] bg-surface border border-border-subtle flex items-center justify-center flex-shrink-0">
                    <Icon name="calendar-day" size={20} className="text-text-secondary" />
                  </div>
                  <div className="flex flex-wrap gap-2 items-baseline text-[16px] text-text-primary">
                    {rootEvent.is_all_day ? (
                      <span className="font-medium">
                        {startDateTime.toFormat("EEEE, MMMM d, yyyy")}
                      </span>
                    ) : (
                      <>
                        <span className="font-medium whitespace-nowrap">
                          {datePart}
                        </span>
                        <span className="whitespace-nowrap">
                          {timeRangePart}
                        </span>
                      </>
                    )}
                    {rootEvent.repeat_rule && (
                      <span className="text-text-secondary whitespace-nowrap">
                        (Repeats {rootEvent.repeat_rule.toLowerCase()})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Location — shared between hang and event */}
          {(hasLocation || rootEvent.is_virtual) && (
            <>
              {rootEvent.is_virtual ? (
                <div className="border border-border-tertiary rounded-[16px] p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-[12px] bg-surface border border-border-subtle flex items-center justify-center flex-shrink-0">
                      <Icon name="link" size={20} className="text-text-secondary" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[16px] font-medium text-text-primary">
                        Virtual Event
                      </span>
                      {rootEvent.virtual_link_url && (
                        <a
                          href={rootEvent.virtual_link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[16px] text-accent-secondary hover:underline"
                        >
                          Join link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <LocationView
                  location={rootEvent.location_object}
                  locationName={rootEvent.location_name}
                />
              )}
            </>
          )}

          {/* Add to calendar */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center justify-center gap-3 rounded-[81px] px-2 py-3.5 border border-border-secondary bg-gradient-to-b from-white to-[#fafaf5] to-[14.9%] opacity-80 hover:opacity-100 transition-all"
                style={{
                  boxShadow: "0px 1px 8px 0px rgba(0,0,0,0.06), inset 0px 2px 3px 0px rgba(255,255,255,0.35)",
                }}
              >
                <Icon name="calendar-plus" size={20} className="text-text-primary" />
                <span className="text-[16px] font-medium text-text-primary">Add to calendar</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem
                onClick={() => {
                  const url = generateGoogleCalendarUrl({
                    title: rootEvent.title || hangTitle || "Event",
                    startDate: event.occurrence_start,
                    endDate: event.occurrence_end,
                    location: rootEvent.location_name || undefined,
                    description: rootEvent.description || undefined,
                    isAllDay: rootEvent.is_all_day,
                  });
                  window.open(url, "_blank");
                }}
              >
                Google Calendar
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  downloadIcsFile({
                    title: rootEvent.title || hangTitle || "Event",
                    startDate: event.occurrence_start,
                    endDate: event.occurrence_end,
                    location: rootEvent.location_name || undefined,
                    description: rootEvent.description || undefined,
                    isAllDay: rootEvent.is_all_day,
                  });
                }}
              >
                Download .ics file
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Description — event only (hang shows description in header) */}
          {!isHang && rootEvent.description && (
            <p className="text-body-2 text-text-secondary whitespace-pre-wrap">
              {rootEvent.description}
            </p>
          )}

          {/* Club card — shared */}
          {club && (
            <div className="border border-border-tertiary rounded-[16px] p-3 flex items-center justify-between gap-4">
              <div className="flex gap-4 items-center min-w-0">
                <div className="w-12 h-12 rounded-[12px] bg-surface border border-border-subtle flex items-center justify-center flex-shrink-0">
                  <Icon name="globe" size={20} className="text-text-secondary" />
                </div>
                <span className="text-[16px] font-medium text-text-primary">
                  {club.name}
                </span>
              </div>
              <button
                className="border border-border-primary rounded-[1000px] px-2 py-[7px] text-[16px] font-medium text-text-primary hover:bg-surface-1 transition-colors whitespace-nowrap flex-shrink-0"
                onClick={() => {
                  onClose?.();
                  navigate({
                    to: "/clubs/$identifier",
                    params: { identifier: club.slug || club.id || "" },
                  });
                }}
              >
                View club
              </button>
            </div>
          )}

          {/* Attendees card — event only */}
          {!isHang && goingCount > 0 && (
            <AttendeesPreviewCard
              goingCount={goingCount}
              goingProfiles={goingProfiles}
              attendeesNamesText={attendeesNamesText}
              currentUserId={currentUserId}
              isManager={isManager}
              eventId={eventId}
            />
          )}

          {/* End/Cancel hang button — at the bottom, visible to creator */}
          {canEndHang && (
            <button
              onClick={() => setShowEndHangDialog(true)}
              className="w-full flex items-center justify-center gap-3 rounded-[81px] px-2 py-3.5 border border-border-secondary bg-gradient-to-b from-white to-[#fafaf5] to-[14.9%] opacity-80 hover:opacity-100 transition-all"
              style={{
                boxShadow: "0px 1px 8px 0px rgba(0,0,0,0.06), inset 0px 2px 3px 0px rgba(255,255,255,0.35)",
              }}
            >
              <span className="text-[16px] font-medium text-red">
                {isOngoing ? "End hang" : "Cancel hang"}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Media viewer modal */}
      {viewerMedia && (
        <WebMediaViewer
          src={viewerMedia.src}
          type={viewerMedia.type}
          onClose={() => setViewerMedia(null)}
        />
      )}

      {/* End/Cancel hang confirmation dialog */}
      <DeleteConfirmDialog
        open={showEndHangDialog}
        onOpenChange={setShowEndHangDialog}
        title={isOngoing ? "End this hang?" : "Cancel this hang?"}
        description={
          rootEvent?.repeat_rule
            ? "This event is part of a sequence. Ending it will remove the entire sequence."
            : "All finished with this hang? It will be removed from everyone's list."
        }
        deleteLabel={isOngoing ? "End hang" : "Cancel hang"}
        deletingLabel="Ending..."
        isDeleting={isDeletingEvent}
        onConfirm={async () => {
          if (!rootEvent?.id || !rootEvent?.club_id) return;
          try {
            await deleteEvent({ eventId: rootEvent.id, clubId: rootEvent.club_id }).unwrap();
            setShowEndHangDialog(false);
            onClose?.();
          } catch (error) {
            console.error("Failed to end hang:", error);
          }
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// EventDetailSidePanel — wraps EventDetailContent in a SidePanel
// ---------------------------------------------------------------------------

interface EventDetailSidePanelProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EventDetailSidePanel({
  eventId,
  open,
  onOpenChange,
}: EventDetailSidePanelProps) {
  const { user } = useAuth();
  const { data: event } = useFetchEventByIdQuery(eventId);
  const rootEvent = event?.rootEvent;
  const isHang = rootEvent?.event_type === EventType.hang;
  const canEdit = !!user?.id && rootEvent?.creator_id === user.id;
  const canShare =
    rootEvent?.allows_public_link !== false && !!event?.invite_code;
  const shareUrl = canShare
    ? `https://tiny.club/e/${event.invite_code}`
    : undefined;

  const mainContent = useMemo(
    () => (
      <SidePanelBody className="px-6 py-0 pb-6">
        <EventDetailContent eventId={eventId} onClose={() => onOpenChange(false)} />
      </SidePanelBody>
    ),
    [eventId, onOpenChange]
  );

  const initialView = useMemo(
    () => ({
      id: "event-detail",
      title: isHang ? "Hang" : "Event details",
      content: mainContent,
    }),
    [mainContent, isHang]
  );

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelStack initialView={initialView}>
          {({ currentView, canGoBack, onBack }) => (
            <>
              <SidePanelHeader
                title={currentView.title}
                onBack={canGoBack ? onBack : undefined}
                onClose={() => onOpenChange(false)}
                actions={
                  currentView.id === "event-detail" && (canEdit || (canShare && shareUrl)) ? (
                    <div className="flex items-center gap-2">
                      {canEdit && rootEvent && (
                        <EditEventAction
                          rootEvent={rootEvent}
                          clubId={rootEvent.club_id ?? ""}
                          isHang={isHang}
                          onClosePanel={() => onOpenChange(false)}
                        />
                      )}
                      {canShare && shareUrl && (
                        <ShareButton
                          url={shareUrl}
                          title={rootEvent?.title || "Check out this event on TinyClub"}
                        />
                      )}
                    </div>
                  ) : undefined
                }
              />
              <SidePanelStackContent />
            </>
          )}
        </SidePanelStack>
      </SidePanelContent>
    </SidePanel>
  );
}

/**
 * Edit button rendered inside the SidePanelStack — uses useSidePanelStack()
 * to push the edit form as a stacked view.
 */
function EditEventAction({
  rootEvent,
  clubId,
  isHang,
  onClosePanel,
}: {
  rootEvent: NonNullable<ReturnType<typeof useFetchEventByIdQuery>["data"]>["rootEvent"];
  clubId: string;
  isHang: boolean;
  onClosePanel: () => void;
}) {
  const { push, pop } = useSidePanelStack();

  if (!rootEvent) return null;

  const handleEdit = () => {
    push({
      id: "edit-event",
      title: isHang ? "Edit Hang" : "Edit Event",
      content: (
        <SidePanelBody className="px-6 py-0 pb-6">
          <EventFormContent
            clubId={clubId}
            creatorId={rootEvent.creator_id ?? ""}
            eventType={isHang ? "hang" : "event"}
            eventId={rootEvent.id}
            existingEvent={rootEvent}
            onClose={() => pop()}
            onDeleted={onClosePanel}
            showHeader={false}
          />
        </SidePanelBody>
      ),
    });
  };

  return (
    <button
      onClick={handleEdit}
      className="h-[40px] min-w-[88px] px-3 rounded-full bg-surface-1 text-[16px] font-medium text-text-secondary hover:bg-surface-2 transition-colors"
    >
      Edit
    </button>
  );
}

// ---------------------------------------------------------------------------
// Attendees Preview Card — clickable to open attendees list
// ---------------------------------------------------------------------------

function AttendeesPreviewCard({
  goingCount,
  goingProfiles,
  attendeesNamesText,
  currentUserId,
  isManager,
  eventId,
}: {
  goingCount: number;
  goingProfiles: UserProfile[];
  attendeesNamesText: string;
  currentUserId?: string;
  isManager: boolean;
  eventId: string;
}) {
  const sidePanelStack = useSidePanelStackOptional();

  const handleViewAttendees = useCallback(() => {
    sidePanelStack?.push({
      id: "attendees",
      title: "Attendees",
      content: (
        <AttendeesListView
          eventId={eventId}
          currentUserId={currentUserId}
          isManager={isManager}
        />
      ),
    });
  }, [sidePanelStack, currentUserId, isManager, eventId]);

  const isClickable = !!sidePanelStack;
  const Tag = isClickable ? "button" : "div";

  return (
    <Tag
      onClick={isClickable ? handleViewAttendees : undefined}
      className={`w-full border border-border-tertiary rounded-[16px] p-3 flex flex-col gap-4 text-left${isClickable ? " hover:bg-surface cursor-pointer" : ""} transition-colors`}
    >
      {/* Header row */}
      <div className="flex flex-col gap-2 w-full">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-[12px] flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "rgba(89,111,70,0.2)" }}
            >
              <span className="text-[18px]">👍</span>
            </div>
            <span className="text-[18px] font-medium text-text-primary">
              {goingCount} Going
            </span>
          </div>
          {isClickable && (
            <Icon
              name="angle-right"
              size={18}
              className="text-text-secondary"
            />
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-border-tertiary" />
      </div>

      {/* Avatar stack + names */}
      {goingProfiles && goingProfiles.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center pr-3">
            {goingProfiles.slice(0, 4).map((profile, index) => (
              <div
                key={profile.id}
                className="w-8 h-8 rounded-full border border-border-tertiary overflow-hidden bg-surface-1 flex-shrink-0"
                style={{ marginLeft: index > 0 ? -12 : 0, zIndex: 10 - index }}
              >
                {profile.avatar_small || profile.avatar_medium ? (
                  <img
                    src={profile.avatar_small || profile.avatar_medium}
                    alt={userDisplayName(profile)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[11px] font-medium text-text-secondary">
                    {(profile.first_name?.[0] || "?").toUpperCase()}
                  </div>
                )}
              </div>
            ))}
          </div>
          {attendeesNamesText && (
            <p className="text-[16px] text-text-secondary leading-[1.3]">
              {attendeesNamesText}
            </p>
          )}
        </div>
      )}
    </Tag>
  );
}

// ---------------------------------------------------------------------------
// Attendees List View — full list of attendees grouped by RSVP status
// ---------------------------------------------------------------------------

// Section header background colors (matching mobile)
const sectionColors = {
  yes: "rgba(89, 111, 70, 0.2)", // Green
  maybe: "rgba(31, 74, 179, 0.2)", // Blue
  no: "rgba(238, 71, 44, 0.2)", // Red
};

function AttendeesListView({
  eventId,
  currentUserId,
  isManager,
}: {
  eventId: string;
  currentUserId?: string;
  isManager: boolean;
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  // Fetch event data directly so we react to cache invalidation
  const { data: event, isLoading: eventLoading } = useFetchEventByIdQuery(eventId);
  const attendees = event?.event_attendees || [];

  // Split attendees by RSVP status
  const goingAttendees = useMemo(
    () => attendees.filter((a: EventAttendee) => a.rsvp_status === RsvpState.yes),
    [attendees]
  );
  const maybeAttendees = useMemo(
    () => attendees.filter((a: EventAttendee) => a.rsvp_status === RsvpState.maybe),
    [attendees]
  );
  const notGoingAttendees = useMemo(
    () => attendees.filter((a: EventAttendee) => a.rsvp_status === RsvpState.no),
    [attendees]
  );

  // Fetch all attendee profiles
  const allAttendeeUserIds = useMemo(
    () => attendees.map((a) => a.user_id).filter(Boolean) as string[],
    [attendees]
  );
  const { data: profiles = [], isLoading: profilesLoading } = useFetchUserProfilesByIdsQuery(
    allAttendeeUserIds,
    { skip: allAttendeeUserIds.length === 0 }
  );

  const isLoading = eventLoading || profilesLoading;

  // RSVP mutation
  const [createAttendee] = useCreateEventAttendeeMutation();

  // Create attendee map for looking up current RSVP status
  const attendeeMap = useMemo(() => {
    const map = new Map<string, EventAttendee>();
    attendees.forEach((a) => {
      if (a.user_id) map.set(a.user_id, a);
    });
    return map;
  }, [attendees]);

  // Handle RSVP update
  const handleRsvpUpdate = useCallback(
    async (userId: string, newStatus: RsvpState) => {
      setUpdatingUserId(userId);
      try {
        await createAttendee({
          occurrenceId: eventId,
          userId,
          rsvpStatus: newStatus,
        });
      } catch (error) {
        console.error("Error updating RSVP:", error);
      } finally {
        setUpdatingUserId(null);
      }
    },
    [eventId, createAttendee]
  );

  // Create profile map
  const profileMap = useMemo(
    () => new Map(profiles.map((p) => [p.id, p])),
    [profiles]
  );

  // Get profiles for each section
  const getProfilesForAttendees = useCallback(
    (attendees: EventAttendee[]) => {
      return attendees
        .map((a) => profileMap.get(a.user_id || ""))
        .filter((p): p is UserProfile => !!p);
    },
    [profileMap]
  );

  const goingProfiles = useMemo(
    () => getProfilesForAttendees(goingAttendees),
    [goingAttendees, getProfilesForAttendees]
  );
  const maybeProfiles = useMemo(
    () => getProfilesForAttendees(maybeAttendees),
    [maybeAttendees, getProfilesForAttendees]
  );
  const notGoingProfiles = useMemo(
    () => getProfilesForAttendees(notGoingAttendees),
    [notGoingAttendees, getProfilesForAttendees]
  );

  // Filter function based on search query
  const filterProfiles = useCallback(
    (profiles: UserProfile[]) => {
      if (!searchQuery.trim()) return profiles;

      const query = searchQuery.toLowerCase();
      return profiles.filter((profile) => {
        const displayName = userDisplayName(profile).toLowerCase();
        const username = profile.username?.toLowerCase() || "";
        return displayName.includes(query) || username.includes(query);
      });
    },
    [searchQuery]
  );

  // Apply filtering to each section
  const filteredGoing = useMemo(
    () => filterProfiles(goingProfiles),
    [goingProfiles, filterProfiles]
  );
  const filteredMaybe = useMemo(
    () => filterProfiles(maybeProfiles),
    [maybeProfiles, filterProfiles]
  );
  const filteredNotGoing = useMemo(
    () => filterProfiles(notGoingProfiles),
    [notGoingProfiles, filterProfiles]
  );

  // Build flat list for keyboard navigation
  const allFilteredProfiles = useMemo(
    () => [...filteredGoing, ...filteredMaybe, ...filteredNotGoing],
    [filteredGoing, filteredMaybe, filteredNotGoing]
  );

  // Handle attendee click
  const handleAttendeeClick = (userId: string) => {
    navigate({ to: "/profile/$userId", params: { userId } });
  };

  const { highlightedIndex, onKeyDown } = useKeyboardNavigation({
    items: allFilteredProfiles,
    onSelect: (profile) => handleAttendeeClick(profile.id),
    enabled: !!searchQuery.trim(),
  });

  const hasResults =
    filteredGoing.length > 0 ||
    filteredMaybe.length > 0 ||
    filteredNotGoing.length > 0;

  return (
    <>
      {/* Search Bar */}
      <div className="flex-shrink-0 px-5 pb-4">
        <div className="flex items-center gap-3 px-4 py-3 rounded-full border border-border-tertiary bg-surface">
          <Icon name="search" size={16} className="text-text-secondary" />
          <input
            type="text"
            placeholder="Search attendees"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={onKeyDown}
            className="flex-1 bg-transparent text-body-2 text-text-primary placeholder:text-text-secondary focus:outline-none"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="p-1 hover:opacity-70 transition-opacity"
            >
              <Icon name="xmark" size={16} className="text-text-tertiary" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <SidePanelBody className="pt-0">
        {isLoading ? (
          <AttendeesListSkeleton />
        ) : !hasResults && searchQuery ? (
          <div className="text-center py-12">
            <p className="text-body-2 text-text-secondary">
              No attendees matching "{searchQuery}"
            </p>
          </div>
        ) : !hasResults ? (
          <div className="flex flex-col items-center py-12 gap-4">
            <div className="w-16 h-16 rounded-xl bg-surface-1 flex items-center justify-center">
              <Icon name="users" size={32} className="text-text-tertiary" />
            </div>
            <p className="text-body-2 text-text-secondary text-center">
              No one has RSVP'd to this event yet.
            </p>
          </div>
        ) : (
          <div className="space-y-0 pb-4">
            {/* Going Section */}
            {filteredGoing.length > 0 && (
              <AttendeeSection
                title="Going"
                emoji="👍"
                color={sectionColors.yes}
                profiles={filteredGoing}
                onAttendeeClick={handleAttendeeClick}
                startIndex={0}
                highlightedIndex={highlightedIndex}
                currentUserId={currentUserId}
                isManager={isManager}
                attendeeMap={attendeeMap}
                onRsvpUpdate={handleRsvpUpdate}
                updatingUserId={updatingUserId}
              />
            )}

            {/* Maybe Section */}
            {filteredMaybe.length > 0 && (
              <AttendeeSection
                title="Maybe"
                emoji="🤷‍♂️"
                color={sectionColors.maybe}
                profiles={filteredMaybe}
                onAttendeeClick={handleAttendeeClick}
                startIndex={filteredGoing.length}
                highlightedIndex={highlightedIndex}
                currentUserId={currentUserId}
                isManager={isManager}
                attendeeMap={attendeeMap}
                onRsvpUpdate={handleRsvpUpdate}
                updatingUserId={updatingUserId}
              />
            )}

            {/* Not Going Section */}
            {filteredNotGoing.length > 0 && (
              <AttendeeSection
                title="Not going"
                emoji="😢"
                color={sectionColors.no}
                profiles={filteredNotGoing}
                onAttendeeClick={handleAttendeeClick}
                startIndex={filteredGoing.length + filteredMaybe.length}
                highlightedIndex={highlightedIndex}
                currentUserId={currentUserId}
                isManager={isManager}
                attendeeMap={attendeeMap}
                onRsvpUpdate={handleRsvpUpdate}
                updatingUserId={updatingUserId}
              />
            )}
          </div>
        )}
      </SidePanelBody>
    </>
  );
}

// Attendee section component
function AttendeeSection({
  title,
  emoji,
  color,
  profiles,
  onAttendeeClick,
  startIndex = 0,
  highlightedIndex = -1,
  currentUserId,
  isManager,
  attendeeMap,
  onRsvpUpdate,
  updatingUserId,
}: {
  title: string;
  emoji: string;
  color: string;
  profiles: UserProfile[];
  onAttendeeClick: (userId: string) => void;
  startIndex?: number;
  highlightedIndex?: number;
  currentUserId?: string;
  isManager: boolean;
  attendeeMap: Map<string, EventAttendee>;
  onRsvpUpdate: (userId: string, newStatus: RsvpState) => void;
  updatingUserId: string | null;
}) {
  return (
    <section className="pt-6 first:pt-0">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: color }}
        >
          <span className="text-[20px]">{emoji}</span>
        </div>
        <h2 className="text-h6 text-text-primary flex-1">
          {title}
        </h2>
        <span className="text-body-2 text-text-secondary">{profiles.length}</span>
      </div>
      {/* Divider */}
      <div className="border-t border-border-tertiary mb-2" />
      {/* Attendee rows */}
      <div className="space-y-1">
        {profiles.map((profile, i) => (
          <AttendeeRow
            key={profile.id}
            profile={profile}
            onClick={() => onAttendeeClick(profile.id)}
            navIndex={startIndex + i}
            highlighted={startIndex + i === highlightedIndex}
            showUpdateButton={isManager || profile.id === currentUserId}
            currentRsvpStatus={attendeeMap.get(profile.id)?.rsvp_status}
            onRsvpUpdate={(newStatus) => onRsvpUpdate(profile.id, newStatus)}
            isUpdating={updatingUserId === profile.id}
          />
        ))}
      </div>
    </section>
  );
}

// RSVP menu options
const rsvpOptions = [
  { status: RsvpState.yes, label: "Going", emoji: "👍" },
  { status: RsvpState.maybe, label: "Maybe", emoji: "🤷‍♂️" },
  { status: RsvpState.no, label: "Not going", emoji: "😢" },
];

// Individual attendee row
function AttendeeRow({
  profile,
  onClick,
  navIndex,
  highlighted = false,
  showUpdateButton,
  currentRsvpStatus,
  onRsvpUpdate,
  isUpdating,
}: {
  profile: UserProfile;
  onClick: () => void;
  navIndex?: number;
  highlighted?: boolean;
  showUpdateButton: boolean;
  currentRsvpStatus?: string;
  onRsvpUpdate: (newStatus: RsvpState) => void;
  isUpdating: boolean;
}) {
  const displayName = userDisplayName(profile);

  return (
    <div
      data-nav-index={navIndex}
      className={cn(
        "w-full flex items-center gap-3 py-2 transition-colors",
        highlighted && "bg-surface"
      )}
    >
      {/* Clickable avatar + name area */}
      <button
        onClick={onClick}
        className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity text-left"
      >
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-border-tertiary">
          {profile.avatar_medium || profile.avatar_small ? (
            <img
              src={profile.avatar_medium || profile.avatar_small || ""}
              alt={displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-surface-1 flex items-center justify-center">
              <Icon name="user" size={16} className="text-text-tertiary" />
            </div>
          )}
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <p className="text-body-2-medium text-text-primary truncate">
            {displayName}
          </p>
        </div>
      </button>

      {/* Update button or chevron */}
      {showUpdateButton ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="px-3 py-1.5 rounded-full bg-surface-1 text-body-2-medium text-text-secondary hover:bg-surface-2 transition-colors flex-shrink-0 disabled:opacity-50"
              disabled={isUpdating}
            >
              {isUpdating ? "..." : "Update"}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {rsvpOptions.map((option) => (
              <DropdownMenuItem
                key={option.status}
                onClick={() => onRsvpUpdate(option.status)}
                className={cn(
                  currentRsvpStatus === option.status && "bg-surface-1"
                )}
              >
                <span className="text-[16px]">{option.emoji}</span>
                <span>{option.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <button
          onClick={onClick}
          className="p-2 hover:opacity-70 transition-opacity flex-shrink-0"
        >
          <Icon name="angle-right" size={16} className="text-text-tertiary" />
        </button>
      )}
    </div>
  );
}

// Skeleton loader for attendees list
function AttendeesListSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-3">
          <Skeleton className="w-10 h-10 rounded-xl" />
          <Skeleton className="h-5 w-16" />
        </div>
        <div className="space-y-1">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[56px] w-full rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RSVP Button
// ---------------------------------------------------------------------------

function RsvpButton({
  emoji,
  label,
  active,
  disabled,
  onClick,
}: {
  emoji: string;
  label: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={cn(
        "flex-1 flex items-center justify-center gap-3 rounded-[81px] px-2 py-3.5 border transition-all",
        active
          ? "border-border-primary bg-surface-1 opacity-100"
          : "border-border-secondary bg-gradient-to-b from-white to-[#fafaf5] to-[14.9%] opacity-80 hover:opacity-100"
      )}
      style={{
        boxShadow: active
          ? "none"
          : "0px 1px 8px 0px rgba(0,0,0,0.06), inset 0px 2px 3px 0px rgba(255,255,255,0.35)",
      }}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="text-[20px] leading-none">{emoji}</span>
      <span className="text-[16px] font-medium text-text-primary">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function EventDetailSkeleton() {
  return (
    <div className="flex flex-col gap-8">
      {/* Image skeleton */}
      <div className="flex justify-center">
        <Skeleton className="w-[336px] max-w-full rounded-[16px]" style={{ aspectRatio: "1 / 1" }} />
      </div>
      <div className="flex flex-col gap-4">
        {/* Title skeleton */}
        <Skeleton className="h-8 w-3/4" />
        {/* Creator row skeleton */}
        <div className="flex items-center gap-2">
          <Skeleton className="w-8 h-8 rounded-full" />
          <Skeleton className="w-8 h-8 rounded-[8px]" />
          <Skeleton className="h-4 w-40" />
        </div>
        {/* RSVP buttons skeleton */}
        <div className="flex gap-2">
          <Skeleton className="h-12 flex-1 rounded-[81px]" />
          <Skeleton className="h-12 flex-1 rounded-[81px]" />
          <Skeleton className="h-12 flex-1 rounded-[81px]" />
        </div>
        {/* Date card skeleton */}
        <Skeleton className="h-[72px] w-full rounded-[16px]" />
        {/* Description skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
        {/* Club card skeleton */}
        <Skeleton className="h-[72px] w-full rounded-[16px]" />
      </div>
    </div>
  );
}

function HangDetailSkeleton() {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        {/* Avatars skeleton */}
        <div className="flex items-start pr-2">
          <Skeleton className="w-8 h-8 rounded-full" />
          <Skeleton className="w-8 h-8 rounded-full ml-[-8px]" />
        </div>
        {/* Title skeleton */}
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-5 w-2/3" />
          {/* Countdown row skeleton */}
          <div className="flex items-center justify-between">
            <Skeleton className="h-10 w-40 rounded-[8px]" />
            <Skeleton className="w-10 h-10 rounded-[8px]" />
          </div>
        </div>
        {/* Location skeleton */}
        <Skeleton className="h-[200px] w-full rounded-[16px]" />
        {/* Club card skeleton */}
        <Skeleton className="h-[72px] w-full rounded-[16px]" />
      </div>
    </div>
  );
}
